import { lesbar } from '../../lib/statusTexte';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Phone, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { MigrationCaseDto } from '@/lib/api';
import { useMigrationOfferHonorarMissing } from '@/lib/migrationOfferHonorar';
import {
  formatSavedAt,
  getMigrationWorkflowState,
  getWizardSavedAt,
  getWizardSnapshot,
  LEGACY_FACH_MASTER,
  type MigrationWorkflowState,
} from '@/lib/migrationCaseWizard';
import {
  formatMigrationEventLabel,
  formatMigrationEventTimestamp,
} from '@/lib/migrationEventDisplay';

const WORKFLOW_STEPS: Array<{ key: MigrationWorkflowState; label: string }> = [
  { key: 'vertragsanpassung', label: 'Vertragsanpassung' },
  { key: 'entscheidung', label: 'Entscheidung' },
  { key: 'anmeldung', label: 'Anmeldung' },
  { key: 'angebot', label: 'Angebot' },
  { key: 'unterricht', label: 'Unterricht' },
];

const WEEKDAY_LABELS: Record<number, string> = {
  1: 'Montag',
  2: 'Dienstag',
  3: 'Mittwoch',
  4: 'Donnerstag',
  5: 'Freitag',
  6: 'Samstag',
  7: 'Sonntag',
};

const normalizeTime = (value: unknown): string | null => {
  const raw = String(value ?? '').trim();
  if (!raw) return null;
  const m = raw.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return `${m[1].padStart(2, '0')}:${m[2]}`;
};

const resolveFachLabel = (fachId: number | null | undefined, fachName: string | null | undefined): string => {
  if (typeof fachId === 'number' && fachId > 0 && LEGACY_FACH_MASTER[fachId]) {
    return LEGACY_FACH_MASTER[fachId];
  }
  const normalized = String(fachName ?? '').trim();
  const fallbackIdMatch = normalized.match(/^Fach\s+#(\d+)$/i);
  if (fallbackIdMatch) {
    const id = Number(fallbackIdMatch[1]);
    return LEGACY_FACH_MASTER[id] ?? normalized;
  }
  if (normalized && normalized !== '(Fach)') {
    return normalized;
  }
  return '(Fach)';
};

type Props = {
  item: MigrationCaseDto;
  isBusy?: boolean;
  allowContractTermination: boolean;
  onLink: (caseId: number) => void;
  onStartContractAdjustment: (caseId: number) => void;
  onStartContractTermination: (caseId: number) => void;
  onStartOrderAdjustment: (caseId: number) => void;
  onSendOrderOffer?: (caseId: number, orderId: number) => void;
  /**
   * „Unterricht bestätigen“: Der Parent öffnet den ConfirmOrderWizardModal
   * (wie im Probestunden-Flow) und bestätigt den angenommenen Auftrag.
   */
  onConfirmOrder?: (caseId: number, enrollmentId: number) => void;
  /**
   * „Kurs starten“: Der Parent öffnet den StartCourseWizardModal, der über die
   * enrollmentId alles selbst lädt und POST /api/enrollments/{id}/start-course
   * aufruft — erst damit ist der Fall migriert.
   */
  onStartCourse?: (caseId: number, enrollmentId: number) => void;
  onToggleFavorite: (caseId: number, nextFavorite: boolean) => void;
  onCall: (caseId: number) => void;
  onCallFailed: (caseId: number) => void;
  onCommunicate: (caseId: number) => void;
  /** Speichert einen Office-Kommentar; Resolve `true` = erfolgreich. */
  onAddComment: (caseId: number, text: string) => Promise<boolean>;
  onOpenHistory: (caseId: number) => void;
};

export default function MigrationCaseGridCard({
  item,
  isBusy = false,
  allowContractTermination,
  onLink,
  onStartContractAdjustment,
  onStartContractTermination,
  onStartOrderAdjustment,
  onSendOrderOffer,
  onConfirmOrder,
  onStartCourse,
  onToggleFavorite,
  onCall,
  onCallFailed,
  onCommunicate,
  onAddComment,
  onOpenHistory,
}: Readonly<Props>) {
  const [commentText, setCommentText] = useState('');
  const [isSavingComment, setIsSavingComment] = useState(false);

  const handleAddComment = async () => {
    const text = commentText.trim();
    if (text === '' || isSavingComment) return;
    setIsSavingComment(true);
    try {
      const ok = await onAddComment(item.id, text);
      if (ok) setCommentText('');
    } finally {
      setIsSavingComment(false);
    }
  };

  const wizard = getWizardSnapshot(item.snapshot);
  const workflow = getMigrationWorkflowState(item);
  const savedAt = formatSavedAt(getWizardSavedAt(wizard));
  const legacy = item.legacy_contract;
  const legacyStudent = legacy?.student;
  const studentLabel = [legacyStudent?.last_name, legacyStudent?.first_name].filter(Boolean).join(', ');
  const studentAge = legacyStudent?.age !== undefined && legacyStudent?.age !== null ? `${legacyStudent.age} Jahre` : '-';
  const snapshot = (item.snapshot as Record<string, unknown> | null) ?? {};
  const weekday = Number(snapshot.legacy_weekday ?? 0);
  const weekdayLabel = WEEKDAY_LABELS[weekday] ?? null;
  const timeLabel = normalizeTime(snapshot.legacy_start_time);
  const weekdayTime = weekdayLabel && timeLabel ? `${weekdayLabel}, ${timeLabel} Uhr` : 'Wochentag, Zeitfenster';
  const fachLabel = resolveFachLabel(legacy?.fach?.id, legacy?.fach?.name);
  const duration = Number(snapshot.legacy_duration_min ?? 0);
  const uformName = String(legacy?.unterrichtsform?.name ?? '').trim();
  const unterrichtsformLabel = uformName
    ? /min\.?/i.test(uformName)
      ? uformName
      : duration > 0
        ? `${uformName} ${duration} Min.`
        : uformName
    : 'Unterrichtsform';
  const latestEvent = item.latest_event ?? null;
  const latestEventLabel = latestEvent ? formatMigrationEventLabel(latestEvent) : null;
  const latestEventTimestamp = latestEvent ? formatMigrationEventTimestamp(latestEvent.created_at) : null;
  const isLinkedOrCreated = Boolean(item.is_linked_or_created || item.person_detail_wp_user_id);
  // Verknüpfen ist nur in der allerersten Phase relevant – sobald das Office
  // den Vertragsanpassungs-Wizard gespeichert hat (Phase != vertragsanpassung),
  // ist die Person verknüpft bzw. angelegt.
  const canShowLinkButton = Boolean(item.email_exists_in_system) && workflow.state === 'vertragsanpassung';
  const canShowOrderAdjustment = item.status === 'migriert';
  const hasUnresolvedEmailConflict = Boolean(item.email_exists_in_system && !isLinkedOrCreated);

  // Order-ID aus Customer-Confirm-Snapshot für „Angebot senden"-Action.
  // Beachte: Bei Customer-Confirm kann createSystemContractFromMigration einen
  // Skip-Branch genommen haben (z. B. fehlende Daten), dann fehlt order_id
  // noch. Der „Angebot senden"-Button wird trotzdem angezeigt – er ruft
  // dann den Recovery-Endpoint `finalizeCase` auf, der die fehlenden
  // Schritte (Enrollment, Order, Versand) idempotent nachholt.
  const customerSnap = (snapshot.customer_contract_adjustment ?? null) as
    | { confirmed_at?: string; finalization?: { order_id?: number | null; enrollment_id?: number | null } }
    | null;
  const finalizationOrderId = (() => {
    const raw = customerSnap?.finalization?.order_id;
    const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const finalizationEnrollmentId = (() => {
    const raw = customerSnap?.finalization?.enrollment_id;
    const n = typeof raw === 'number' ? raw : Number(raw ?? 0);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const customerConfirmed = Boolean(customerSnap?.confirmed_at);
  const canSendOrderOffer = Boolean(onSendOrderOffer && customerConfirmed && workflow.state === 'angebot');
  // Nach Versand soll der Button die Sekundär-Aktion „Angebot anpassen"
  // anbieten (statt erneut zu senden) und dezenter wirken.
  const offerAlreadySent = workflow.angebotStatus === 'gesendet'
    || workflow.angebotStatus === 'angenommen'
    || workflow.angebotStatus === 'abgeschlossen';
  const { loading: honorarLoading, missing: honorarMissing, message: honorarMissingMessage } = useMigrationOfferHonorarMissing(
    item,
    Boolean(canSendOrderOffer && !offerAlreadySent),
  );
  // „Unterricht bestätigen" erscheint, sobald der Lehrer den Auftrag
  // angenommen hat (angebotStatus='angenommen') und der Office-Confirm
  // noch aussteht. Analog zum Anmelde-Flow (TrialLessonGridCard).
  // Wir brauchen die enrollment_id, weil der StartCourseWizardModal sich
  // alle weiteren Daten via enrollmentId selbst zieht.
  // „Kurs starten“ wie bei den Probestunden: sobald die Lehrkraft angenommen hat,
  // bis der Kurs läuft (Status „migriert“).
  const canStartCourse = Boolean(
    onStartCourse
    && workflow.state === 'unterricht'
    && item.status !== 'migriert'
    && finalizationEnrollmentId !== null
  );
  const canConfirmOrder = Boolean(
    onConfirmOrder
    && workflow.angebotStatus === 'angenommen'
    && finalizationEnrollmentId !== null
  );
  const showContractAdjustmentCta = workflow.state === 'vertragsanpassung';
  const showContractAdjustmentSecondary = workflow.state === 'entscheidung' || workflow.state === 'anmeldung';

  return (
    <div className="space-y-4 rounded-2xl border border-border bg-card p-4 text-card-foreground shadow-sm">
      <div className="flex flex-wrap gap-2 border-b border-border pb-3">
        {WORKFLOW_STEPS.map((step, index) => {
          const active = index === workflow.stepIndex;
          const done = index < workflow.stepIndex;
          return (
            <div
              key={step.key}
              className={cn(
                'inline-flex min-w-[120px] items-center justify-center rounded-full px-3 py-1 text-xs font-semibold',
                done
                  ? 'bg-primary/90 text-primary-foreground'
                  : active
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
              )}
            >
              {done ? `✓ ${step.label}` : step.label}
            </div>
          );
        })}
      </div>
      <div className="grid gap-4 md:grid-cols-[2.2fr_1fr_2.4fr_1.8fr]">
        <section className="space-y-2 md:border-r md:border-border md:pr-4">
          <div className="text-xs font-semibold text-muted-foreground">Infos</div>
          <div className="space-y-1 text-sm leading-tight">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="icon"
                variant={item.is_favorite ? 'default' : 'outline'}
                className="h-7 w-7 rounded-full"
                onClick={() => onToggleFavorite(item.id, !item.is_favorite)}
                disabled={isBusy}
                title={item.is_favorite ? 'Favorit entfernen' : 'Favorit setzen'}
                aria-label={item.is_favorite ? 'Favorit entfernen' : 'Favorit setzen'}
              >
                <Star className="h-3.5 w-3.5" />
              </Button>
              <Badge className="inline-flex rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground hover:bg-primary">
                {fachLabel}
              </Badge>
            </div>
            <div className="font-semibold">
              {isLinkedOrCreated && item.person_detail_wp_user_id ? (
                <Link to={`/dashboard/people/${item.person_detail_wp_user_id}`} className="hover:underline">
                  {studentLabel || 'Unbekannt'}
                </Link>
              ) : (
                <span>{studentLabel || 'Unbekannt'}</span>
              )}{' '}
              ({studentAge})
            </div>
            <div>{unterrichtsformLabel}</div>
            <div>{weekdayTime}</div>
            <div>{legacy?.teacher?.name ?? 'Lehrername'}</div>
            {item.email_exists_in_system ? (
              <Badge variant="warning" className="w-fit">
                E-Mail bereits im System vorhanden
              </Badge>
            ) : null}
            <div className="pt-1 text-[11px] text-muted-foreground">
              Fall #{item.id} · {lesbar(item.migration_phase ?? 'vertragsanpassung_offen')} · {lesbar(item.status)}
            </div>
          </div>
        </section>

        <section className="space-y-2 md:border-r md:border-border md:pr-4">
          <div className="text-xs font-semibold text-muted-foreground">Kontakt</div>
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-secondary-foreground"
              onClick={() => onCall(item.id)}
              disabled={isBusy}
              title="Anrufen"
            >
              <Phone className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-destructive"
              onClick={() => onCallFailed(item.id)}
              disabled={isBusy}
              title="Nicht erreicht"
            >
              <Phone className="h-4 w-4" />
            </button>
            <button
              type="button"
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-secondary text-secondary-foreground"
              onClick={() => onCommunicate(item.id)}
              disabled={isBusy}
              title="Nachricht senden"
            >
              <MessageSquare className="h-4 w-4" />
            </button>
          </div>
        </section>

        <section className="space-y-2 md:border-r md:border-border md:pr-4">
          <div className="text-xs font-semibold text-muted-foreground">Ereignisse</div>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => onOpenHistory(item.id)}
              title="Komplette Historie öffnen"
              className="block w-full rounded-lg bg-muted px-3 py-2 text-left text-sm transition-colors hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {latestEvent ? (
                <div className="flex items-start justify-between gap-2">
                  <span className="line-clamp-2 min-w-0 flex-1 break-words">{latestEventLabel}</span>
                  {latestEventTimestamp ? (
                    <span className="shrink-0 text-[11px] text-muted-foreground">{latestEventTimestamp}</span>
                  ) : null}
                </div>
              ) : (
                <span className="text-muted-foreground">Noch keine Ereignisse</span>
              )}
            </button>
            <div className="flex items-center gap-1">
              <Input
                className="h-10 flex-1"
                placeholder="Kommentar hinzufügen"
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    void handleAddComment();
                  }
                }}
                disabled={isSavingComment}
              />
              <Button
                type="button"
                className="h-10 w-10 rounded-lg p-0"
                disabled={commentText.trim() === '' || isSavingComment}
                onClick={() => void handleAddComment()}
              >
                +
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-2 pt-1">
              <div className="rounded-lg bg-muted/50 px-3 py-2 text-center">
                <div className="text-[11px] font-semibold text-muted-foreground">Anmeldung</div>
                <div
                  className={cn(
                    'text-xs',
                    workflow.anmeldungStatus === 'abgeschlossen'
                      ? 'font-semibold text-green-600'
                      : 'text-muted-foreground'
                  )}
                >
                  {lesbar(workflow.anmeldungStatus)}
                </div>
              </div>
              <div className="rounded-lg bg-muted/50 px-3 py-2 text-center">
                <div className="text-[11px] font-semibold text-muted-foreground">Angebot</div>
                <div
                  className={cn(
                    'text-xs',
                    workflow.angebotStatus === 'abgeschlossen'
                      ? 'font-semibold text-green-600'
                      : workflow.angebotStatus === 'angenommen'
                        // ANG-6: angenommen ist eine positive Phase (Lehrer
                        // hat zugesagt) — grün statt amber.
                        ? 'font-semibold text-green-600'
                        : 'text-muted-foreground'
                  )}
                >
                  {lesbar(workflow.angebotStatus)}
                </div>
              </div>
            </div>
            {savedAt ? (
              <div className="text-[11px] text-muted-foreground">Save: {savedAt}</div>
            ) : null}
          </div>
        </section>

        <section className="space-y-2">
          <div className="text-xs font-semibold text-muted-foreground">Funktionen</div>
          <div className="space-y-1">
            {canShowLinkButton ? (
              <Button size="sm" variant="secondary" className="w-full justify-start" onClick={() => onLink(item.id)} disabled={isBusy}>
                Datensatz verknüpfen
              </Button>
            ) : null}

            {canSendOrderOffer && !offerAlreadySent ? (
              <Button
                size="sm"
                className="w-full justify-start"
                onClick={() => onSendOrderOffer?.(item.id, finalizationOrderId ?? 0)}
                disabled={isBusy || honorarLoading || honorarMissing}
                title={honorarMissing
                  ? honorarMissingMessage
                  : (finalizationOrderId === null
                    ? 'Vertrag wird jetzt finalisiert (Enrollment + Auftrag) und an den Lehrer versendet.'
                    : 'Auftrag an den Lehrer versenden.')}
              >
                Angebot senden
              </Button>
            ) : null}

            {canConfirmOrder ? (
              <Button
                size="sm"
                className="w-full justify-start"
                onClick={() => onConfirmOrder?.(item.id, finalizationEnrollmentId ?? 0)}
                disabled={isBusy}
                title="Lehrer hat angenommen — jetzt bestätigen, damit der Unterricht aktiv wird."
              >
                Unterricht bestätigen
              </Button>
            ) : null}

            {canStartCourse ? (
              <Button
                size="sm"
                variant={canConfirmOrder ? 'secondary' : 'default'}
                className="w-full justify-start"
                onClick={() => onStartCourse?.(item.id, finalizationEnrollmentId ?? 0)}
                disabled={isBusy}
                title="Kursteilnahme anlegen und den reservierten Kurs aktivieren."
              >
                Kurs starten
              </Button>
            ) : null}

            {canSendOrderOffer && offerAlreadySent ? (
              <Button
                size="sm"
                variant="outline"
                className="w-full justify-start text-muted-foreground"
                onClick={() => onStartOrderAdjustment(item.id)}
                disabled={isBusy}
                title="Auftrag/Angebot anpassen (z. B. Termin, Lehrer, Raum)."
              >
                Angebot anpassen
              </Button>
            ) : null}

            {showContractAdjustmentCta ? (
              <Button
                size="sm"
                className="w-full justify-start"
                onClick={() => onStartContractAdjustment(item.id)}
                disabled={isBusy || hasUnresolvedEmailConflict}
                title={hasUnresolvedEmailConflict ? 'Vor Start zuerst Datensatz verknüpfen oder neu anlegen.' : undefined}
              >
                Vertragsanpassung starten
              </Button>
            ) : null}

            {showContractAdjustmentSecondary ? (
              <Button
                size="sm"
                variant="secondary"
                className="w-full justify-start"
                onClick={() => onStartContractAdjustment(item.id)}
                disabled={isBusy || hasUnresolvedEmailConflict}
                title={hasUnresolvedEmailConflict ? 'Vor Start zuerst Datensatz verknüpfen oder neu anlegen.' : undefined}
              >
                Vertrag anpassen
              </Button>
            ) : null}

            {canShowOrderAdjustment && workflow.state !== 'angebot' ? (
              <Button
                size="sm"
                variant="default"
                className="w-full justify-start"
                onClick={() => onStartOrderAdjustment(item.id)}
                disabled={isBusy}
              >
                Auftrag anpassen
              </Button>
            ) : null}

            {allowContractTermination && item.status !== 'migriert' && item.status !== 'beendet' ? (
              <Button size="sm" variant="secondary" className="w-full justify-start" onClick={() => onStartContractTermination(item.id)} disabled={isBusy}>
                Vertrag beenden
              </Button>
            ) : null}
          </div>
        </section>
      </div>
    </div>
  );
}
